// components/tier-branch/AddBranchToTierDialog.tsx
// Quick "Add Branch" form shown from a tier card (like the video's tier rows).
// Typing offers the branches that exist but belong to no tier yet — picking one
// attaches it, rather than creating a second branch with the same name. A name
// that matches nothing still creates a new branch. Full details are edited
// later in Branch Management.
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Switch, Box, CircularProgress, Autocomplete, Typography,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Tier, Branch, BranchInput } from "../../types/tierBranch";
import type { Notify } from "./useSnack";

// Sensible defaults for the fields not shown in the quick form.
const BRANCH_DEFAULTS: Omit<BranchInput, "name" | "enabled" | "tierId"> = {
  whatsappNo: "",
  whatsappLink: "",
  creditLimit: 0,
  maxNewUsers: 0,
  maxDeposit: 0,
  registrationDelaySeconds: 0,
  registrationLocked: false,
  betLocked: false,
  depositLocked: false,
  withdrawLocked: false,
  batchedWithdrawal: false,
  notes: "",
};

const AddBranchToTierDialog: React.FC<{
  open: boolean;
  tier: Tier | null;
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}> = ({ open, tier, onClose, onSaved, notify }) => {
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<Branch[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setActive(true);

    // A branch belongs to at most one tier, so anything already assigned is not
    // on offer here. Moving one between tiers is a Branch Management job.
    let alive = true;
    setLoadingOptions(true);
    api
      .listBranches({ unassigned: true, limit: 100 })
      .then((r) => { if (alive) setOptions(r.data); })
      .catch(() => { if (alive) setOptions([]); })
      .finally(() => { if (alive) setLoadingOptions(false); });
    return () => { alive = false; };
  }, [open]);

  // An exact name match means "attach that branch", not "create a twin".
  const matched = useMemo(
    () =>
      options.find(
        (b) => b.name.trim().toLowerCase() === name.trim().toLowerCase()
      ) || null,
    [options, name]
  );

  const handleSave = async () => {
    if (!name.trim()) {
      notify("Branch name is required", "warning");
      return;
    }
    if (!tier) return;
    setSaving(true);
    try {
      if (matched) {
        await api.updateBranch(matched.id, { tierId: tier.id, enabled: active });
        notify(`${matched.name} attached to ${tier.name}`, "success");
      } else {
        await api.createBranch({
          ...BRANCH_DEFAULTS,
          name: name.trim(),
          enabled: active,
          tierId: tier.id,
        });
        notify("Branch added", "success");
      }
      onSaved();
      onClose();
    } catch (e: any) {
      notify(e?.message || "Failed to add branch", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{tier ? `Add Branch to ${tier.name}` : "Add Branch"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Autocomplete
            freeSolo
            options={options}
            loading={loadingOptions}
            getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
            inputValue={name}
            onInputChange={(_, v) => setName(v)}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2">{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{o.id}
                    {o.userCount ? ` • ${o.userCount} users` : ""}
                    {o.enabled ? "" : " • disabled"}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Branch Name"
                size="small"
                fullWidth
                autoFocus
                helperText={
                  matched
                    ? `Attaches existing branch #${matched.id}`
                    : loadingOptions
                    ? "Loading available branches…"
                    : options.length
                    ? `${options.length} unassigned branch(es) available — or type a new name`
                    : "No unassigned branches — a new one will be created"
                }
              />
            )}
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Add Branch
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBranchToTierDialog;
